// ────────────────────────────────────────────────────────────────────
// InboxScreen — cross-tenant staff triage (PR 1 + PR 2 of
// docs/superpowers/specs/2026-07-23-cross-tenant-inbox.md). Front-style
// 3-pane: left = one row per tenant with activity, sorted awaiting-reply-
// oldest-first then recency (server-side, see server/routers/inbox.py);
// middle = the selected tenant's conversation, rendered via the SAME
// TeamChatBody the customer dock uses — "one component, two hosts", not a
// fork; right (PR 2, ≥1100px only) = InboxContextPane, open งาน + recent
// completed for that same tenant.
//
// Row selection = switching the staff active tenant, via the EXACT mechanism
// the existing tenant switcher (AppShell.tsx's TenantSwitcher) already uses:
// setActiveTenant + a full page reload. That's not an oversight — reusing the
// same full-reload-on-switch routine is what guarantees tenant A's
// conversation can never flash under tenant B's row: the ["conversation"]
// query is never re-fetched-in-place for a different tenant, it's simply
// never alive across the switch. Selection also lives in the URL (?t=) so a
// refresh restores the same row/pane instead of losing it.
// ────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox as InboxIcon } from "lucide-react";
import { apiClient, getActiveTenant, setActiveTenant, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { subscribeLiveEvents } from "@/lib/liveEvents";
import { TeamChatBody } from "@/components/TeamChatDock";
import { InboxContextPane } from "@/components/InboxContextPane";
import type { InboxRow } from "@/lib/types";

// Fallback poll — SSE (below) is the primary channel, but it only carries
// pokes for the CURRENTLY bound tenant's stream (the stream endpoint itself
// is tenant-scoped, same as the conversation query it drives — see
// portal/src/lib/liveEvents.ts's module docstring). A poke on some OTHER
// tenant in the list never reaches this browser tab, so this poll is what
// eventually surfaces "the 3rd customer down just messaged" without a
// refresh. 30s matches TeamChatBody's own visible-poll cadence.
const INBOX_POLL_MS = 30_000;

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return "เมื่อสักครู่";
  if (mins < 60) return `${mins} นาทีที่แล้ว`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} ชม.ที่แล้ว`;
  const days = Math.floor(hrs / 24);
  return `${days} วันที่แล้ว`;
}

// Hours since awaiting_reply_since, floored, never negative (clock skew /a
// just-computed timestamp landing a beat in the "future" degrades to 0
// rather than a nonsensical negative badge).
export function awaitingHours(iso: string): number {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 3_600_000));
}

function sidePrefix(side: InboxRow["last_side"]): string {
  if (side === "client") return "ลูกค้า: ";
  if (side === "staff") return "ทีม: ";
  return "";
}

// Exported (2026-07-24 workbench shell) so TodayScreen's staff "บทสนทนาที่
// ยังไม่ตอบ" section renders the EXACT same badge instead of a re-styled copy.
export function AwaitingBadge({ since }: { since: string }): JSX.Element {
  const h = awaitingHours(since);
  return (
    <span className="font-mono uppercase inline-flex items-center shrink-0" style={{
      fontSize: 8.5, letterSpacing: ".06em", fontWeight: 700, color: "var(--warn)",
      border: "1px solid rgba(180,83,9,0.4)", borderRadius: 3, padding: "1px 5px",
      background: "rgba(180,83,9,0.08)" }}>
      รอตอบ {h < 1 ? "<1" : h}ชม.
    </span>
  );
}

function OpenCountChip({ n }: { n: number }): JSX.Element | null {
  if (n <= 0) return null;
  return (
    <span className="font-mono inline-flex items-center shrink-0" style={{
      fontSize: 9, color: "var(--ink-3)", border: "1px solid var(--hairline)",
      borderRadius: 3, padding: "1px 5px" }}>
      {n} งาน
    </span>
  );
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-center justify-center h-full font-mono text-center px-4"
         style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
      {children}
    </div>
  );
}

export default function InboxScreen(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getCachedUser();
  const isStaff = !!user && (STAFF_ROLES as readonly string[]).includes(user.role);

  // Staff-only screen — same redirect-away pattern JobScreen already
  // uses for a งาน (house precedent for a screen the backend also
  // 403s a customer out of).
  useEffect(() => { if (!isStaff) navigate("/", { replace: true }); }, [isStaff]);   // eslint-disable-line react-hooks/exhaustive-deps

  const urlTenant = searchParams.get("t");

  // Keep the URL and the active-tenant localStorage key in sync, in EITHER
  // direction, without ever soft-switching in place:
  //   - URL names a tenant the browser isn't currently bound to (a shared
  //     inbox link, or a stale param from before a manual switch elsewhere)
  //     -> adopt it via the real switch routine (reload) so every other
  //     tenant-scoped query in the app (not just this screen) picks it up.
  //   - No `t` param yet, but a tenant IS already active -> just reflect it
  //     in the URL (no reload needed, nothing actually changed).
  useEffect(() => {
    if (!isStaff) return;
    const active = getActiveTenant();
    if (urlTenant && urlTenant !== active) {
      setActiveTenant(urlTenant);
      window.location.reload();
      return;
    }
    if (!urlTenant && active) {
      setSearchParams({ t: active }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  const listQ = useQuery({
    queryKey: ["inbox"], queryFn: () => apiClient.inbox(),
    enabled: isStaff, staleTime: 0, refetchInterval: INBOX_POLL_MS,
  });
  const rows: InboxRow[] = listQ.data ?? [];

  // SSE (roadmap 2.1 channel, reused): a poke means SOMETHING moved on the
  // currently-bound tenant's thread — refetch both this list (its preview/
  // awaiting badge for that row may have changed) and the open conversation
  // pane. See INBOX_POLL_MS above for why this alone doesn't cover every row.
  useEffect(() => {
    if (!isStaff) return;
    const unsubscribe = subscribeLiveEvents((kind) => {
      if (kind !== "conversation") return;
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["conversation"] });
    });
    return unsubscribe;
  }, [isStaff, qc]);

  const activeTenant = getActiveTenant();
  const selectedId = activeTenant && /^\d+$/.test(activeTenant) ? Number(activeTenant) : null;

  const selectTenant = (id: number) => {
    if (String(id) === activeTenant) {
      setSearchParams({ t: String(id) });
      return;
    }
    // Same mechanism as AppShell.tsx's TenantSwitcher — full reload, on
    // purpose (see the module docstring above).
    setActiveTenant(id);
    const url = new URL(window.location.href);
    url.searchParams.set("t", String(id));
    window.location.href = url.toString();
  };

  if (!isStaff) return <Empty>—</Empty>;

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      <div className="px-6 py-4 border-b flex items-center gap-2.5" style={{ borderColor: "var(--hairline)" }}>
        <InboxIcon size={16} style={{ color: "var(--accent)" }} />
        <div>
          <div className="font-mono uppercase mb-0.5" style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--accent-deep)", fontWeight: 600 }}>
            / TRIAGE — ทุกลูกค้า
          </div>
          <h1 className="font-display font-medium m-0" style={{ fontSize: 22, color: "var(--ink-1)", letterSpacing: "-.02em" }}>
            Inbox
          </h1>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* left — conversation list */}
        <div className="w-[340px] shrink-0 border-r flex flex-col min-h-0" style={{ borderColor: "var(--hairline)" }}>
          <div className="flex-1 overflow-auto">
            {listQ.isPending && <Empty>กำลังโหลด…</Empty>}
            {listQ.isError && <Empty>โหลดไม่สำเร็จ ลองใหม่</Empty>}
            {!listQ.isPending && !listQ.isError && rows.length === 0 && <Empty>ยังไม่มีการสนทนา</Empty>}
            {rows.map((r) => {
              const selected = r.tenant_id === selectedId;
              return (
                <button key={r.tenant_id} onClick={() => selectTenant(r.tenant_id)}
                        data-testid={`inbox-row-${r.tenant_id}`}
                        className="w-full text-left px-4 py-3 border-b transition-colors hover:bg-[rgba(31,78,121,0.04)]"
                        style={{ borderColor: "var(--hairline)",
                                 background: selected ? "rgba(var(--accent-rgb) / 0.07)" : undefined,
                                 borderLeft: `2px solid ${selected ? "var(--accent)" : "transparent"}` }}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-mono truncate" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>
                      {r.name}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.awaiting_reply_since && <AwaitingBadge since={r.awaiting_reply_since} />}
                      <OpenCountChip n={r.open_submissions} />
                    </div>
                  </div>
                  <div className="font-mono truncate mb-1" style={{ fontSize: 11, color: "var(--ink-3)" }}>
                    {r.last_preview ? `${sidePrefix(r.last_side)}${r.last_preview}` : "ยังไม่มีข้อความ"}
                  </div>
                  <div className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
                    {relTime(r.last_event_at)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* middle — the selected tenant's conversation (embedded TeamChatBody) */}
        <div className="flex-1 min-w-0 flex flex-col">
          {selectedId == null ? (
            <Empty>เลือกลูกค้าทางซ้ายเพื่อเริ่มแชท</Empty>
          ) : (
            <TeamChatBody key={selectedId} visible />
          )}
        </div>

        {/* right — customer context pane (PR 2): open งาน + recent completed,
            reading the same ["conversation"] query as the middle pane (see
            InboxContextPane's module docstring). Hidden below 1100px — the
            2-pane layout (list + conversation) is the floor, this is a bonus
            on wide screens, not something worth a real responsive drawer for
            v1. */}
        <div className="hidden min-[1100px]:flex w-[300px] shrink-0 border-l flex-col min-h-0"
             style={{ borderColor: "var(--hairline)" }}>
          {selectedId == null
            ? <Empty>เลือกลูกค้าทางซ้ายก่อน</Empty>
            : <InboxContextPane key={selectedId} />}
        </div>
      </div>
    </div>
  );
}
